import React from 'react';
import CircularProgress from '@mui/material/CircularProgress';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { useSearchParams } from 'react-router';
import { useSession } from '../../../firebase/UserProvider';
import { useAllReports } from '../hooks/useAllReports';
import { useSavedFilters } from '../hooks/useSavedFilters';
import { useFilterParam } from '../hooks/useFilterParam';
import AllProductsPageInner from './AllProductsPageInner';

export const MAX_FILTER_NAME_LENGTH = 128;

/**
 * Outer shell — waits for user + savedFilters before rendering.
 * Single loading gate: no intermediate wrong state reaches the UI.
 * URL-synchronisation logic is delegated to useFilterParam.
 */
const AllProductsPage = () => {
    const { user, role } = useSession();
    const { reports, loading: reportsLoading, error: reportsError, refresh: refreshReports } = useAllReports();
    const [searchParams, setSearchParams] = useSearchParams();

    const {
        filters: savedFilters,
        save: saveFilter,
        update: updateFilter,
        rename: renameFilter,
        remove: removeFilter,
        reorder: reorderFilters,
        loading: filtersLoading,
    } = useSavedFilters(user?.uid, 'products');

    const { setFilterParam, handleSaveAsFilter } = useFilterParam(
        savedFilters,
        filtersLoading,
        searchParams,
        setSearchParams,
        saveFilter,
    );

    if (!user || filtersLoading) return (
        <Stack flex={1} alignItems="center" justifyContent="center">
            <CircularProgress />
        </Stack>
    );

    if (reportsError) return (
        <Stack p={3}>
            <Typography color="error.main">Failed to load products. Please refresh.</Typography>
        </Stack>
    );

    return (
        <AllProductsPageInner
            user={user}
            viewerMode={role === 'policymaker'}
            reports={reports}
            reportsLoading={reportsLoading}
            onReportsRefresh={refreshReports}
            maxFilterNameLength={MAX_FILTER_NAME_LENGTH}
            savedFilters={savedFilters}
            onSaveAsFilter={handleSaveAsFilter}
            updateFilter={updateFilter}
            renameFilter={renameFilter}
            removeFilter={removeFilter}
            reorderFilters={reorderFilters}
            searchParams={searchParams}
            setSearchParams={setSearchParams}
        />
    );
};

export default AllProductsPage;
